import { useEffect, useState } from 'react';

import toast from 'react-hot-toast';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Yup from '../../../utils/yupExtensions';
import styles from './JobseekerWelcomeForms.module.css'
import FormField from '../../UI/UpdatedFormUI/FormField/FormField';
import BubbleButton from '../../UI/Buttons/BubbleButton/BubbleButton';
import FormOptionField from '../../UI/UpdatedFormUI/FormOptionField/FormOptionField';
import { fetchUserByToken } from '../../../app/features/userSlice';
import { signup, socialSignup } from '../../../services/authService';

function CreateAccountForm(props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user.user);
    const userData = useSelector((state) => state.user.userData);
    const [formLoading, setFormLoading] = useState(false);
    const navigate = useNavigate();
    useEffect(() => {
        if (user) {
            navigate('/welcome');
        }
    }, [navigate, user]);

    const formik = useFormik({
        initialValues: {
            first_name: userData?.name?.split(' ')[0] ? userData?.name?.split(' ')[0] : '',
            last_name: userData?.name.split(' ')[1] ? userData?.name?.split(' ')[1] : '',
            email: userData?.email || '',
            password: '',
            company_name: '',
            show_verified_jobSeeker: false,
            account_type: props?.accountType

        },
        validationSchema: Yup.object({
            first_name: Yup.string()
                .required(t('forms.create_account.required')),
            last_name: Yup.string()
                .required(t('forms.create_account.required')),
            email: Yup.string()
                .email(t('forms.create_account.invalid_email_address'))
                .required(t('forms.create_account.required')),
            password: Yup.string()
                .min(8, t('forms.create_account.must_be_at_least_8_characters'))
                .required(t('forms.create_account.required')),
            show_verified_jobSeeker: Yup.boolean()
                .oneOf([true], t('show_verified_jobSeeker_required')),
            company_name: props.company && Yup.string()
                .required(t('forms.create_account.required'))
        }),
        onSubmit: async (values) => {
            if (formLoading) {
                return;
            }
            setFormLoading(true);

            toast.promise(
                userData ?
                    socialSignup({ ...userData, password: values.password, ...values, jobseeker_status: props?.seletectEntity }, userData.provider, values.account_type) :
                    signup({ ...values, jobseeker_status: props?.seletectEntity }), {
                loading: t('forms.create_account.creating_account'),
                success: <b>{t('forms.create_account.account_created_successfully')}</b>,
                error: (err) => {
                    return <b>{err.response.data.error}</b>;
                },
            })
                .then((response) => {
                    toast.promise(dispatch(fetchUserByToken(response)), {
                        loading: t('forms.create_account.logging_in'),
                        success: <b>{t('forms.create_account.successfully_logged_in')}</b>,
                        error: (err) => {
                            return <b>{err.response.data.error}</b>;
                        },
                    })
                })
                .catch((error) => {

                    if (error?.response?.data?.field) {
                        formik.setErrors({
                            [error.response.data.field]: error.response.data.error
                        });
                    }

                    setFormLoading(false);

                });

        },
    });
    useEffect(() => {
        if (userData?.name) {
            formik.setFieldValue('first_name', userData?.name?.split(' ')[0] ? userData?.name?.split(' ')[0] : '');
            formik.setFieldValue('last_name', userData?.name.split(' ')[1] ? userData?.name?.split(' ')[1] : '');
            formik.setFieldValue('email', userData?.email);

        }
    }, [userData])
    return (
        <form onSubmit={formik.handleSubmit} {...props} className={styles.formContainer}>
            <div className={styles.formRow}>
                <div className={styles.formFieldWrapper}>
                    <FormField
                        isAuth
                        name="first_name"
                        type="text"
                        placeholder={t('forms.create_account.first_name')}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.first_name}
                        error={formik.touched.first_name && formik.errors.first_name}
                    />
                </div>
                <div className={styles.formFieldWrapper}>
                    <FormField
                        isAuth
                        name="last_name"
                        type="text"
                        placeholder={t('forms.create_account.last_name')}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.last_name}
                        error={formik.touched.last_name && formik.errors.last_name}
                    />
                </div>
            </div>
            <div>
                <FormField
                    isAuth
                    name="email"
                    type="email"
                    placeholder={t('forms.create_account.email')}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                    error={formik.touched.email && formik.errors.email}
                />
            </div>
            <div>
                <FormField
                    isAuth
                    name="password"
                    type="password"
                    placeholder={t('forms.create_account.password')}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.password}
                    error={formik.touched.password && formik.errors.password}
                />
            </div>
            {props.company &&
                <div>
                    <FormField
                        isAuth
                        name="company_name"
                        type="text"
                        placeholder={t('forms.create_account.company_name')}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.company_name}
                        error={formik.touched.company_name && formik.errors.company_name}
                    />
                </div>
            }
            <div>
                <FormOptionField
                    name="show_verified_jobSeeker"
                    dark
                    type="checkbox"
                    options={[
                        {
                            value: true,
                            label: (
                                <>
                                    {t('forms.create_account.show_verified_jobSeeker_label')}{' '}
                                    <div onClick={() => { }} href="/terms-and-conditions" className={styles.loginLink}>
                                        {t('forms.terms_and_conditions')}
                                    </div>{' '}
                                    {t('forms.create_account.findcv')}
                                </>
                            )
                        },
                    ]}
                    onChange={formik.handleChange}
                    value={formik.values.show_verified_jobSeeker}
                    error={formik.touched.show_verified_jobSeeker && formik.errors.show_verified_jobSeeker}
                />
            </div>
            <div className={styles.checkboxWrapper}>
                <BubbleButton
                    dark
                    headerDarkButton
                    onClick={formik.handleSubmit}
                    type="submit"
                    className={styles.bubbleButtonWrapper}
                >
                    {t('forms.create_account.signup')}
                </BubbleButton>
            </div>
            <p className={styles.signupText}>
                {t('forms.create_account.alreadyRegistered')}

                <span onClick={() => {
                    navigate('/login')
                }} className={styles.loginLink}>
                    {t('forms.create_account.here')}
                </span>
                {t('forms.create_account.to_login')}
            </p>
        </form>
    );
}

export default CreateAccountForm;
